import { Stack } from "@mui/material";
import { DynamicRecordDrawer } from "features/shared/components/dynamic-form";
import { formatTechnicalConfigDrawerSubtitle } from "features/shared/lib/technical-config-tab/drawerFormatters";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { LayeredConstructionApi } from "../api/createLayeredConstructionApi";
import type { ConstructionGroup } from "../model/types";
import type { LayeredConstructionVariant } from "../model/variants";

import { useLayeredConstructionForm } from "../hooks/useLayeredConstructionForm";
import { normalizeTechnology } from "../lib/catalogMappers";
import { ConstructionLayers } from "./ConstructionLayers";

type ConfigDrawerProps = {
  api: LayeredConstructionApi;
  group: ConstructionGroup | null;
  onClose: () => void;
  open: boolean;
  projectId: string;
  variant: LayeredConstructionVariant;
};

export const ConfigDrawer = ({ api, group, onClose, open, projectId, variant }: ConfigDrawerProps) => {
  const { t } = useTranslation();

  const userDefinedType = group?.userDefinedType;

  const { data: detail, isLoading } = api.useGetDetail(projectId, userDefinedType);
  const { data: catalog = [], isLoading: isCatalogLoading } = api.useGetCatalog(open);
  const { isLoading: isSaving, mutate: saveConfig } = api.useSaveConfig();
  const { isLoading: isDeleting, mutate: deleteConfig } = api.useDeleteConfig();

  const technology = detail?.technology;
  const initialTechnology = useMemo(
    () => (technology ? normalizeTechnology(technology, variant) : undefined),
    [technology, variant]
  );
  const form = useLayeredConstructionForm({
    catalog,
    enabled: open,
    hasTechnology: group?.hasTechnology ?? false,
    initialTechnology,
    isCatalogLoading,
    isLoading,
    recordKey: userDefinedType,
    variant,
  });

  const handleSave = () => {
    if (!userDefinedType || !group) return;
    saveConfig(
      { data: form.mapPayload(), hasTechnology: group.hasTechnology, projectId, userDefinedType },
      { onSuccess: onClose }
    );
  };

  const handleDelete = () => {
    if (!userDefinedType || !group?.hasTechnology) return;
    deleteConfig({ projectId, userDefinedType }, { onSuccess: onClose });
  };

  const i18nPrefix = `TechnicalData.${variant.i18nKey}`;
  return (
    <DynamicRecordDrawer
      isDeleting={isDeleting}
      isLoading={isLoading || isCatalogLoading}
      isSubmitting={isSaving}
      mode={group?.hasTechnology ? "edit" : "add"}
      onClose={onClose}
      onDelete={group?.hasTechnology ? handleDelete : undefined}
      onDeleteLabel={String(t(`${i18nPrefix}.removeConfig`))}
      onSave={handleSave}
      open={open}
      subtitle={
        group
          ? formatTechnicalConfigDrawerSubtitle(
              group.amount,
              String(t(`${i18nPrefix}.${variant.countLabelKey}`)),
              group.totalArea
            )
          : undefined
      }
      title={group?.userDefinedType ?? String(t(`${i18nPrefix}.drawerTitle`))}
      widthClassName="w-[60rem]"
    >
      <Stack className="h-full w-full flex-col items-stretch gap-4 overflow-y-auto pr-1">
        <ConstructionLayers
          formState={form.formState}
          materialNames={form.materialNames}
          onAddLayer={form.addLayer}
          onColorChange={form.setColor}
          onRemoveLayer={form.removeLayer}
          onSelectLayerMaterial={form.selectLayerMaterial}
          onSelectType={form.selectType}
          onUpdateLayer={form.updateLayer}
          t={t}
          totalThicknessMillimeters={technology?.thicknessTotal ?? null}
          typeNames={form.typeNames}
          uValue={technology?.uValue ?? null}
          variant={variant}
        />
      </Stack>
    </DynamicRecordDrawer>
  );
};
